import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSwitchTenant } from '@/hooks/use-auth';
import { useCreateTenant } from '@/hooks/use-tenants';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

interface CreateWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Cria nova workspace + opcionalmente troca pra ela. Se user marcar "trocar
 * agora", após criar o `useSwitchTenant` é disparado — o reload da
 * página pega o novo tenant ativo.
 */
export function CreateWorkspaceDialog({ open, onOpenChange }: CreateWorkspaceDialogProps) {
  const [name, setName] = useState('');
  const [switchAfter, setSwitchAfter] = useState(true);
  const create = useCreateTenant();
  const switchTenant = useSwitchTenant();

  function reset(): void {
    setName('');
    setSwitchAfter(true);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const result = await create.mutateAsync({ name: trimmed });
      toast.success(`Workspace "${result.name}" criada`);
      onOpenChange(false);
      reset();
      if (switchAfter) {
        // useSwitchTenant cuida do reload + overlay.
        await switchTenant.mutateAsync(result.id);
      }
    } catch {
      // toast já feito em onError do hook
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova workspace</DialogTitle>
          <DialogDescription>
            Cria um novo workspace independente. Você vira owner — todos os dados (leads, campanhas,
            conexões) ficam isolados das outras workspaces.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off" noValidate>
          <div className="space-y-2">
            <Label htmlFor="ws-name">Nome do workspace</Label>
            <Input
              id="ws-name"
              autoFocus
              required
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Cliente XPTO, Equipe Vendas, Projeto Recovery…"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={switchAfter}
              onChange={(e) => setSwitchAfter(e.target.checked)}
              className="h-4 w-4"
            />
            <span>Trocar pra essa workspace após criar</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending ? 'Criando…' : 'Criar workspace'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
